import { computed, ref, type Ref } from "vue";

type EnabledFlag = boolean | Ref<boolean> | (() => boolean);

export type TaskConfig = {
  id: string;
  label?: string;
  intervalMs: number;
  run: () => void | Promise<void>;
  enabled?: EnabledFlag;
  staleAfterMs?: number;
};

type TaskState = {
  id: string;
  label: string;
  intervalMs: number;
  staleAfterMs: number;
  lastStartedAt: number | null;
  lastOkAt: number | null;
  lastErrorAt: number | null;
  lastErrorMessage: string | null;
  inFlight: boolean;
};

type InternalTask = TaskConfig & {
  state: TaskState;
};

function resolveEnabled(enabled: EnabledFlag | undefined): boolean {
  if (enabled === undefined) return true;
  if (typeof enabled === "function") return enabled();
  if (typeof enabled === "boolean") return enabled;
  return enabled.value;
}

/**
 * Periodic task scheduler used for low-frequency refresh/resync and UI timebases.
 *
 * Historically this was the "poll" orchestrator; WS-first state sync now uses it primarily as a
 * safety-net resync cadence rather than an HTTP polling loop.
 */
export function useTaskOrchestrator(options?: { minTickMs?: number; maxTickMs?: number }) {
  const minTickMs = Math.max(25, Math.floor(options?.minTickMs ?? 80));
  const maxTickMs = Math.max(minTickMs, Math.floor(options?.maxTickMs ?? 2000));

  const tasks = ref<InternalTask[]>([]);
  const running = ref(false);
  const paused = ref(false);
  let timer: number | null = null;

  function registerTask(config: TaskConfig) {
    const existing = tasks.value.find((t) => t.id === config.id);
    if (existing) return;

    const staleAfterMs = Math.max(0, Math.floor(config.staleAfterMs ?? config.intervalMs * 2.5));
    const state: TaskState = {
      id: config.id,
      label: config.label ?? config.id,
      intervalMs: Math.max(25, Math.floor(config.intervalMs)),
      staleAfterMs,
      lastStartedAt: null,
      lastOkAt: null,
      lastErrorAt: null,
      lastErrorMessage: null,
      inFlight: false
    };
    tasks.value = [...tasks.value, { ...config, intervalMs: state.intervalMs, staleAfterMs, state }];
  }

  function unregisterTask(id: string) {
    tasks.value = tasks.value.filter((t) => t.id !== id);
  }

  function clearTimer() {
    if (timer === null) return;
    window.clearTimeout(timer);
    timer = null;
  }

  async function runTask(task: InternalTask) {
    if (task.state.inFlight) return;
    task.state.inFlight = true;
    task.state.lastStartedAt = Date.now();
    task.state.lastErrorMessage = null;
    try {
      const result = task.run();
      if (result && typeof (result as Promise<void>).then === "function") {
        await result;
        task.state.lastOkAt = Date.now();
      } else {
        task.state.lastOkAt = Date.now();
        task.state.inFlight = false;
        return;
      }
    } catch (error) {
      task.state.lastErrorAt = Date.now();
      task.state.lastErrorMessage = error instanceof Error ? error.message : String(error);
    } finally {
      task.state.inFlight = false;
    }
  }

  async function tick() {
    if (!running.value) return;
    if (paused.value) {
      scheduleNext(maxTickMs);
      return;
    }

    const now = Date.now();
    const due: InternalTask[] = [];

    for (const task of tasks.value) {
      if (!resolveEnabled(task.enabled)) continue;
      const last = task.state.lastStartedAt ?? 0;
      if (now - last >= task.intervalMs) due.push(task);
    }

    for (const task of due) {
      void runTask(task);
    }

    scheduleNext();
  }

  function scheduleNext(forcedDelayMs?: number) {
    clearTimer();
    if (!running.value) return;

    if (typeof forcedDelayMs === "number") {
      timer = window.setTimeout(tick, Math.max(minTickMs, Math.floor(forcedDelayMs)));
      return;
    }

    const now = Date.now();
    let nextDueMs = maxTickMs;

    for (const task of tasks.value) {
      if (!resolveEnabled(task.enabled)) continue;
      const last = task.state.lastStartedAt ?? 0;
      const dueAt = last + task.intervalMs;
      const wait = Math.max(0, dueAt - now);
      nextDueMs = Math.min(nextDueMs, wait);
    }

    timer = window.setTimeout(tick, Math.max(minTickMs, Math.min(maxTickMs, nextDueMs)));
  }

  function start() {
    if (running.value) return;
    running.value = true;
    scheduleNext(0);
  }

  function stop() {
    running.value = false;
    clearTimer();
  }

  function setPaused(value: boolean) {
    paused.value = value;
  }

  async function trigger(id: string) {
    const task = tasks.value.find((t) => t.id === id);
    if (!task) return;
    if (!resolveEnabled(task.enabled)) return;
    await runTask(task);
  }

  const taskStates = computed(() => tasks.value.map((t) => t.state));
  const anyStale = computed(() => {
    const now = Date.now();
    return taskStates.value.some((t) => {
      const ok = t.lastOkAt ?? t.lastStartedAt;
      if (!ok) return false;
      return now - ok > t.staleAfterMs;
    });
  });

  const anyInFlight = computed(() => taskStates.value.some((t) => t.inFlight));
  const lastOkAt = computed(() => Math.max(0, ...taskStates.value.map((t) => t.lastOkAt ?? 0)));

  return {
    running,
    paused,
    taskStates,
    anyInFlight,
    anyStale,
    lastOkAt,
    registerTask,
    unregisterTask,
    start,
    stop,
    trigger,
    setPaused
  };
}


